import { getToken } from 'firebase/messaging';
import { getMessagingInstance } from '../auth/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Global flag to track if we've already tried to initialize
let _initAttempted = false;
let swReadyPromise: Promise<void> | null = null;

// Cache VAPID key after fetching it
let cachedVAPIDKey: string | null = null;
let vapidKeyPromise: Promise<string | null> | null = null;

/**
 * Get VAPID key from secure Cloud Function
 * This ensures the key is never exposed in client-side code
 */
async function getVAPIDKeyFromServer(): Promise<string | null> {
  // Return cached key if available
  if (cachedVAPIDKey) {
    return cachedVAPIDKey;
  }

  // Return existing promise if one is already in flight
  if (vapidKeyPromise) {
    return vapidKeyPromise;
  }

  // Create new promise to fetch VAPID key
  vapidKeyPromise = (async () => {
    try {
      const functions = getFunctions();
      const wixAdminCallable = httpsCallable(functions, 'wixAdmin');

      const result = await wixAdminCallable({
        collection: 'notifications',
        method: 'getVAPIDKey',
        params: {}
      }) as { data: { success: boolean; vapidKey?: string; error?: string } };

      if (result.data.success && result.data.vapidKey) {
        cachedVAPIDKey = result.data.vapidKey;
        console.log('✅ VAPID key retrieved from server, length:', result.data.vapidKey.length);
        console.log('✅ VAPID key (first 30 chars):', result.data.vapidKey.substring(0, 30) + '...');
        return cachedVAPIDKey;
      } else {
        console.error('❌ Failed to get VAPID key from server:', result.data.error);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching VAPID key from server:', error);
      return null;
    }
  })();

  return vapidKeyPromise;
}

/**
 * Initialize Firebase messaging - Vite PWA's service worker will handle it
 * Just wait for the service worker to be ready, Firebase will use it for push
 */
export function initializeServiceWorkerForNotifications(): void {
  // Only attempt once
  if (_initAttempted) {
    return;
  }
  _initAttempted = true;

  // Start waiting for Vite PWA's service worker in background
  swReadyPromise = (async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn('🔔 Service Workers not supported - notifications will not work');
      return;
    }

    try {
      // Wait for Vite PWA's service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      
      if (registration && registration.active) {
        console.log('✅ Service worker ready for push notifications');
        console.log('   Scope:', registration.scope);
        console.log('   URL:', registration.active.scriptURL);
      } else {
        console.warn('⚠️ Service worker registered but not active yet');
      }
    } catch (error) {
      console.warn('⚠️ Error waiting for service worker:', error);
    }
  })();
}

interface RequestNotificationPermissionOptions {
  prompt?: boolean;
}

/**
 * Request notification permission explicitly.
 * Must be called inside a user gesture (click, keypress, etc.) when prompting is desired.
 * Returns the permission state after request.
 */
export async function requestNotificationPermission(options: RequestNotificationPermissionOptions = {}): Promise<NotificationPermission> {
  const { prompt = false } = options;

  if (!('Notification' in window)) {
    console.warn('🔔 Notifications not supported');
    return 'denied';
  }

  if (Notification.permission !== 'default') {
    console.log('🔔 Permission already set to:', Notification.permission);
    return Notification.permission;
  }

  if (!prompt) {
    console.log('🔔 Skipping notification permission prompt');
    return Notification.permission;
  }

  console.log('🔔 Requesting notification permission...');
  try {
    const permission = await Notification.requestPermission();
    console.log('🔔 Permission result:', permission);
    return permission;
  } catch (error) {
    console.error('❌ Error requesting notification permission:', error);
    return Notification.permission;
  }
}

/**
 * Get FCM token for the device
 * This token is used to send push notifications to this specific device
 * WILL NOT FAIL - continues with login even if token generation fails
 * NOTE: Permission should be requested separately inside a user gesture
 */
export async function getFCMToken(): Promise<string | null> {
  try {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.warn('🔔 Notifications not supported by browser - continuing without notifications');
      return null;
    }

    // Check if permission is granted
    if (Notification.permission !== 'granted') {
      console.warn('🔔 Notification permission not granted - skipping FCM token');
      return null;
    }

    // Check if we're on a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      console.warn('🔔 Not in secure context (HTTPS required for push) - skipping FCM token');
      return null;
    }

    // Ensure service worker is ready (wait for Vite PWA's SW)
    if (swReadyPromise) {
      try {
        await Promise.race([
          swReadyPromise,
          new Promise<void>((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000))
        ]);
      } catch {
        console.warn('⚠️ Service worker not ready yet - FCM may fail but continuing');
      }
    }

    console.log('🔔 Permission state adequate, requesting FCM token...');

    // Get VAPID key from Cloud Function (stored securely in Firebase Console env vars)
    const vapidKey = await getVAPIDKeyFromServer();
    if (!vapidKey) {
      console.error('❌ Cannot get VAPID key from server - notifications will not work');
      return null;
    }

    console.log('🔔 Using VAPID key for FCM token request...');
    console.log('   VAPID Key length:', vapidKey.length);
    console.log('   VAPID Key starts with:', vapidKey.substring(0, 20));
    console.log('   VAPID Key ends with:', vapidKey.substring(vapidKey.length - 20));
    
    // Verify the VAPID key format
    if (!/^[A-Za-z0-9_-]+$/.test(vapidKey)) {
      console.warn('⚠️ VAPID key contains unexpected characters');
    }

    // Use the VitePWA service worker that now includes Firebase messaging
    console.log('🔔 Getting VitePWA service worker registration (includes Firebase)...');
    const swRegistration = await navigator.serviceWorker.ready;
    console.log('✅ Service worker ready at scope:', swRegistration.scope);

    // Check if push manager is available
    if (!swRegistration.pushManager) {
      console.error('❌ Push manager not available on service worker - likely not HTTPS or secure context');
      return null;
    }

    // Check for existing subscription but DON'T unsubscribe - Firebase needs consistency
    try {
      const existingSubscription = await swRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('🔔 Found existing push subscription');
        console.log('   Endpoint:', existingSubscription.endpoint.substring(0, 50) + '...');
        // If we already have a subscription, try to get the token for it
        // Don't unsubscribe as this can cause push service errors
      } else {
        console.log('🔔 No existing push subscription found');
      }
    } catch (error) {
      console.warn('⚠️ Error checking existing subscription:', error);
    }

    console.log('🔔 Calling getToken()...');
    console.log('   Current domain:', window.location.hostname);
    console.log('   Current protocol:', window.location.protocol);
    console.log('   Is secure context:', window.isSecureContext);
    console.log('   SW scope:', swRegistration.scope);

    try {
      console.log('🔔 Calling getToken with VAPID key...');
      console.log('   VAPID Key length:', vapidKey.length);
      console.log('   VAPID Key first 20 chars:', vapidKey.substring(0, 20));
      
      const messagingInstance = getMessagingInstance();
      if (!messagingInstance) {
        console.error('❌ Firebase messaging instance not initialized');
        return null;
      }
      
      // In production, use the dedicated Firebase messaging SW.
      // In dev, fall back to the VitePWA SW (push may not work in dev - acceptable).
      let firebaseSWReg: ServiceWorkerRegistration = swRegistration;
      try {
        const existing = await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope');
        if (existing) {
          firebaseSWReg = existing;
          console.log('✅ Reusing existing Firebase messaging SW');
        } else {
          firebaseSWReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/firebase-cloud-messaging-push-scope'
          });
          console.log('✅ Registered Firebase messaging SW');
        }
      } catch {
        console.warn('⚠️ Firebase messaging SW not available (dev mode), using VitePWA SW');
      }

      const tokenPromise = getToken(messagingInstance, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: firebaseSWReg
      });

      console.log('🔔 getToken promise created, awaiting...');

      const token = await Promise.race([
        tokenPromise,
        new Promise<string | null>((_, reject) => 
          setTimeout(() => reject(new Error('Token request timeout')), 20000)
        )
      ]) as string | null;

      if (token) {
        console.log('✅ FCM token obtained:', token.substring(0, 20) + '...');
        return token;
      } else {
        console.warn('❌ No token received - continuing without notifications');
        return null;
      }
    } catch (innerError) {
      console.error('❌ getToken() failed:', innerError);
      
      // Provide more detailed context about the error
      if (innerError instanceof Error) {
        console.error('   Error name:', innerError.name);
        console.error('   Error message:', innerError.message);
        
        // Check if it's a credential/permission issue
        if (innerError.name === 'AbortError' && innerError.message.includes('push service error')) {
          console.error('   → Push service registration failed');
          console.error('   → This may indicate a VAPID key or Firebase config issue');
          console.error('   → Verify FIREBASE_VAPID_KEY is set correctly in Cloud Console');
        }
        
        // Check for transient Firebase messaging error
        if (innerError.message?.includes('token-subscribe-failed')) {
          console.warn('   → FCM subscription failed (transient error - this is normal during initial load)');
          console.warn('   → Notifications will be retried on next session');
        }
      }
      // Don't re-throw - return null to allow login to continue
      return null;
    }
  } catch (error) {
    // CRITICAL: Never throw - notifications must never break login
    console.error('❌ Error getting FCM token:', error);
    
    // More detailed error logging
    if (error instanceof Error) {
      console.error('  Error name:', error.name);
      console.error('  Error message:', error.message);
    }
    
    console.error('Error details:', {
      name: error instanceof Error ? error.name : 'unknown',
      message: error instanceof Error ? error.message : String(error),
      isSecureContext: window.isSecureContext,
      protocol: window.location.protocol,
      hostname: window.location.hostname
    });
    console.warn('⚠️ Notification error caught - login will continue without notifications');
    return null;
  }
}

/**
 * Store device token via Firebase Cloud Function
 */
export async function storeDeviceTokenViaFunction(userId: string, deviceToken: string, deviceName: string = 'Web App'): Promise<boolean> {
  try {
    // Validate inputs before making the call
    if (!userId || !deviceToken) {
      console.error('❌ Cannot store device token - missing required parameters:', {
        userId: userId ? 'present' : 'MISSING',
        deviceToken: deviceToken ? 'present' : 'MISSING',
        deviceName
      });
      return false;
    }

    console.log('📱 Storing device token...', {
      userId: userId.substring(0, 8) + '...',
      tokenLength: deviceToken.length,
      deviceName
    });

    const functions = getFunctions();
    const wixAdminCallable = httpsCallable(functions, 'wixAdmin');

    const result = await wixAdminCallable({
      collection: 'deviceTokens',
      method: 'storeDeviceToken',
      params: {
        userId,
        deviceToken,
        deviceName
      }
    }) as { data: { success: boolean; error?: string } };

    if (result.data.success) {
      console.log('✅ Device token stored successfully');
      return true;
    } else {
      console.warn('❌ Failed to store device token:', result.data.error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error storing device token via function:', error);
    return false;
  }
}

/**
 * Remove device token on logout
 */
export async function removeDeviceTokenViaFunction(userId: string, deviceToken: string): Promise<boolean> {
  try {
    const functions = getFunctions();
    const wixAdminCallable = httpsCallable(functions, 'wixAdmin');

    const result = await wixAdminCallable({
      collection: 'deviceTokens',
      method: 'removeDeviceToken',
      params: {
        userId,
        deviceToken
      }
    }) as { data: { success: boolean; error?: string } };

    if (result.data.success) {
      console.log('✅ Device token removed successfully');
      return true;
    } else {
      console.warn('❌ Failed to remove device token:', result.data.error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error removing device token via function:', error);
    return false;
  }
}

/**
 * Get notifications for a user via Cloud Function
 */
export async function getNotificationsViaFunction(userId: string): Promise<{
  success: boolean;
  notifications?: Record<string, unknown>[];
  error?: string;
}> {
  try {
    if (!userId) {
      return {
        success: false,
        error: 'userId is required'
      };
    }

    const functions = getFunctions();
    const wixAdminCallable = httpsCallable(functions, 'wixAdmin');

    const result = await wixAdminCallable({
      collection: 'notifications',
      method: 'getNotifications',
      params: { userId }
    }) as { data: { success: boolean; notifications?: Record<string, unknown>[]; error?: string } };

    if (result.data.success && result.data.notifications) {
      console.log(`✅ Found ${result.data.notifications.length} notifications for user ${userId}`);
      return {
        success: true,
        notifications: result.data.notifications
      };
    } else {
      console.error('❌ Failed to fetch notifications:', result.data.error);
      return {
        success: false,
        error: result.data.error
      };
    }
  } catch (error) {
    console.error('❌ Error fetching notifications via function:', error);
    return {
      success: false,
      error: String(error)
    };
  }
}

